import { Table, Badge, Form, Button, Modal, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useContext } from 'react';
import { getAllJgTeams, createJgTeams, getJgTeamStudents, JgTeamStudentsResponse, getTeamsMemberQueue, TeamsMemberQueueResponse } from '../../services/api';
import { JgTeam } from '../../entities/JgTeam';
import { InfoCircle } from 'react-bootstrap-icons';
import { UserContext } from '../../userContext';

export default function JgTeams() {
  const { t } = useTranslation('common');
  const user = useContext(UserContext);
  const [teams, setTeams] = useState<JgTeam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTeamNames, setSelectedTeamNames] = useState<string[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [teamsToCreate, setTeamsToCreate] = useState<string[]>([]);
  const [editingTeamName, setEditingTeamName] = useState<string | null>(null);
  const [tempTeamName, setTempTeamName] = useState<string>('');
  const [creatingTeams, setCreatingTeams] = useState(false);
  const [showStudentsDialog, setShowStudentsDialog] = useState(false);
  const [studentsData, setStudentsData] = useState<JgTeamStudentsResponse | null>(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [syncData, setSyncData] = useState<TeamsMemberQueueResponse | null>(null);
  const [loadingSyncData, setLoadingSyncData] = useState(false);
  const [filter, setFilter] = useState({
    status: '',
    year: ''
  });
  const [filteredTeams, setFilteredTeams] = useState<JgTeam[]>([]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      const teamsData = await getAllJgTeams();
      setTeams(teamsData);
      setFilteredTeams(teamsData);
      setError(null);
    } catch (err) {
      setError(t('errors.loadingTeams'));
      console.error('Error fetching teams:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncData = async () => {
    try {
      setLoadingSyncData(true);
      const syncStatusData = await getTeamsMemberQueue({});
      setSyncData(syncStatusData);
    } catch (err) {
      console.error('Error fetching sync data:', err);
      // Error loading sync status is not critical
    } finally {
      setLoadingSyncData(false);
    }
  };

  useEffect(() => {
    fetchTeams();
    // Sync data nur für Admins laden (Teams-Admin oder System-Admin)
    if (user?.role === "admin" || user?.teamsRole?.includes("admin")) {
      fetchSyncData();
    }
  }, []);

  useEffect(() => {
    // Filter teams based on selected filters
    let result = [...teams];
    
    if (filter.status) {
      result = result.filter(team => team.status === filter.status);
    }
    
    if (filter.year) {
      result = result.filter(team => {
        return getYearFromTeamName(team.name) === filter.year;
      });
    }
    
    setFilteredTeams(result);
  }, [filter, teams]);

  const handleCreate = async (teamName: string) => {
    setTeamsToCreate([teamName]);
    setShowConfirmDialog(true);
  };

  const handleCreateSelectedTeams = () => {
    setTeamsToCreate(selectedTeamNames);
    setShowConfirmDialog(true);
  };

  const handleShowStudents = async (teamName: string) => {
    try {
      setLoadingStudents(true);
      setShowStudentsDialog(true);
      const data = await getJgTeamStudents(teamName);
      setStudentsData(data);
    } catch (err) {
      setError(t('errors.loadingStudents'));
      console.error('Error fetching students:', err);
      setShowStudentsDialog(false);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleCloseStudentsDialog = () => {
    setShowStudentsDialog(false);
    setStudentsData(null);
  };

  const handleConfirmCreate = async () => {
    try {
      setCreatingTeams(true);
      await createJgTeams(teamsToCreate);
      await fetchTeams();
      // Sync-Daten nach Team-Erstellung auch aktualisieren (nur für Admins)
      if (user?.role === "admin" || user?.teamsRole?.includes("admin")) {
        fetchSyncData();
      }
      setSelectedTeamNames([]);
      setError(null);
      setShowConfirmDialog(false);
    } catch (err) {
      setError(t('errors.creatingTeams'));
      console.error('Error creating teams:', err);
    } finally {
      setCreatingTeams(false);
    }
  };

  const getYearFromTeamName = (teamName: string): string => {
    return teamName.slice(-4);
  };

  const getConfirmationMessage = (): string => {
    const years = Array.from(new Set(teamsToCreate.map(getYearFromTeamName)));
    const yearsList = years.join(', ');
    
    const teamsList = teamsToCreate
      .map(team => `• ${team}`)
      .join('\n');
    
    return `Achtung:
Nach dem Anlegen der Teams-Jahrgangsgruppe werden Sie als BesitzerIn und die Studierenden des Jahrgangs ${yearsList} (Jahr der Imatrikulation) direkt in den Kurs eingeschrieben und per Mail informiert. Studierende eines anderen Jahrgangs, die aufgrund der flexiblen Lehre auch Zugang zu diesem Jahrgangskurs brauchen, müssen direkt in der Teams-Gruppe manuell von dem/der BesitzerIn eingeschrieben und informiert werden.

Folgende Teams werden angelegt:
${teamsList}

Sind Sie sicher das der/die ausgewählten Gruppen jetzt angelegt werden sollen:`;
  };
  const handleCheckboxChange = (teamName: string) => {
    setSelectedTeamNames(prevSelected => {
      if (prevSelected.includes(teamName)) {
        return prevSelected.filter(name => name !== teamName);
      } else {
        return [...prevSelected, teamName];
      }
    });
  };

  const tableRowStyle = {
    height: '56px'
  };

  const tableCellStyle = {
    verticalAlign: 'middle' as const
  };

  // Get unique years from team creation dates
  const getAvailableYears = () => {
    const years = teams.map(team => getYearFromTeamName(team.name));
    return Array.from(new Set(years)).sort();
  };

  const getTeamSyncStatus = (teamId: string | null) => {
    if (loadingSyncData) return {
      mainStatus: { status: t('syncStatus.loading'), variant: 'secondary', details: null },
      errorStatus: null
    };
    if (!syncData) return {
      mainStatus: { status: t('syncStatus.notAvailable'), variant: 'secondary', details: null },
      errorStatus: null
    };

    if (!teamId) {
      return {
        mainStatus: { status: t('syncStatus.notAvailable'), variant: 'secondary', details: null },
        errorStatus: null
      };
    }

    // Find the team in sync data by team_id (viel robuster als team_name!)
    const teamSyncInfo = syncData.teams.find(team => team.team_id === teamId);

    if (!teamSyncInfo) {
      return {
        mainStatus: { status: t('syncStatus.notSynchronized'), variant: 'secondary', details: null },
        errorStatus: null
      };
    }
    
    // API gibt Strings zurück - zu Numbers konvertieren
    const total_members = parseInt(teamSyncInfo.total_members.toString());
    const completed = parseInt(teamSyncInfo.completed.toString());
    const pending = parseInt(teamSyncInfo.pending.toString());
    const failed = parseInt(teamSyncInfo.failed.toString());
    
    // Processing berechnen: total_members - completed - pending - failed
    const processing = Math.max(0, total_members - completed - pending - failed);
    
    // Hauptstatus basierend auf aktueller Aktivität (processing > pending > completed)
    let mainStatus;
    
    if (processing > 0) {
      mainStatus = {
        status: t('syncStatus.synchronizing'),
        variant: 'info',
        details: pending > 0
          ? t('syncStatus.detailsProcessingWithPending', { completed, total_members, processing, pending })
          : t('syncStatus.detailsProcessing', { completed, total_members, processing })
      };
    } else if (pending > 0) {
      mainStatus = {
        status: t('syncStatus.waitingForSync'),
        variant: 'warning',
        details: t('syncStatus.detailsPending', { completed, total_members, pending })
      };
    } else if (completed === total_members && total_members > 0) {
      mainStatus = {
        status: t('syncStatus.fullySynchronized'),
        variant: 'success',
        details: t('syncStatus.detailsSuccess', { completed, total_members })
      };
    } else if (completed > 0) {
      mainStatus = {
        status: t('syncStatus.partiallySynchronized'),
        variant: 'info',
        details: t('syncStatus.detailsSuccess', { completed, total_members })
      };
    } else {
      mainStatus = { status: t('syncStatus.starting'), variant: 'secondary', details: null };
    }
    
    // Error status as separate badge
    const errorStatus = failed > 0 ? {
      status: t('syncStatus.errorCount', { count: failed }),
      variant: 'danger',
      details: t('syncStatus.failedSynchronizations', { count: failed })
    } : null;
    
    return { mainStatus, errorStatus };
  };

  if (loading) {
    return <div>Lade Teams...</div>;
  }

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  return (
    <div>
      <h2 className="mb-4">JG-Kurse beantragen</h2>
      
      <div className="d-flex gap-2 align-items-center mb-3">
        <span>{selectedTeamNames.length} Datensätze gewählt</span>
        <Button 
          variant="primary"
          onClick={handleCreateSelectedTeams}
          disabled={selectedTeamNames.length === 0}
        >
          Teams anlegen
        </Button>
      </div>

      <div className="d-flex flex-wrap flex-md-nowrap align-items-center pt-3 pb-3 mb-3 border-bottom">
        <span>Status: </span>
        <Form.Select
          value={filter.status}
          className="ms-3"
          style={{ width: '200px' }}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="">Alle</option>
          <option value="offen">Offen</option>
          <option value="bereits angelegt">Bereits angelegt</option>
        </Form.Select>
        
        <span className="ms-3">Jahr: </span>
        <Form.Select
          value={filter.year}
          className="ms-3"
          style={{ width: '150px' }}
          onChange={(e) => setFilter({ ...filter, year: e.target.value })}
        >
          <option value="">Alle</option>
          {getAvailableYears().map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Form.Select>
      </div>

      <Table className="table">
        <thead>
          <tr>
            <th scope="col"></th>
            <th scope="col">Ersteller</th>
            <th scope="col">Status</th>
            <th scope="col">
              Sync Status
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="sync-status-tooltip">
                    <div style={{ textAlign: 'left' }}>
                      <strong>{t('syncStatus.legend.title')}:</strong><br/>
                      <strong>{t('syncStatus.legend.mainStatus')}:</strong><br/>
                      <Badge bg="success" className="me-1">{t('syncStatus.badges.green')}</Badge> {t('syncStatus.badges.greenDescription')}<br/>
                      <Badge bg="info" className="me-1">{t('syncStatus.badges.blue')}</Badge> {t('syncStatus.badges.blueDescription')}<br/>
                      <Badge bg="warning" className="me-1">{t('syncStatus.badges.yellow')}</Badge> {t('syncStatus.badges.yellowDescription')}<br/>
                      <Badge bg="secondary" className="me-1">{t('syncStatus.badges.gray')}</Badge> {t('syncStatus.badges.grayDescription')}<br/>
                      <strong>{t('syncStatus.badges.errorAdditional')}:</strong><br/>
                      <Badge bg="danger" className="me-1">{t('syncStatus.badges.red')}</Badge> {t('syncStatus.badges.redDescription')}
                    </div>
                  </Tooltip>
                }
              >
                <InfoCircle 
                  className="ms-1" 
                  style={{ cursor: 'pointer', fontSize: '14px' }}
                />
              </OverlayTrigger>
            </th>
            <th scope="col">Team Name</th>
            <th scope="col">Erstellungsdatum</th>
            <th scope="col"></th>
          </tr>
        </thead>
        <tbody>
          {filteredTeams.map((team) => (
            <tr key={team.name} style={tableRowStyle}>
              <td style={tableCellStyle}>
                <Form.Check
                  type="checkbox"
                  checked={selectedTeamNames.includes(team.name)}
                  onChange={() => handleCheckboxChange(team.name)}
                  disabled={team.status === 'bereits angelegt'}
                />
              </td>
              <td style={tableCellStyle}>{team.createdBy}</td>
              <td style={tableCellStyle}>
                <Badge bg={team.status === 'offen' ? 'primary' : 'success'}>
                  {team.status}
                </Badge>
              </td>
              <td style={tableCellStyle}>
                {(() => {
                  const { mainStatus, errorStatus } = getTeamSyncStatus(team.teamId);
                  return (
                    <div className="d-flex gap-1 flex-wrap">
                      <Badge 
                        bg={mainStatus.variant} 
                        title={mainStatus.details || undefined}
                        style={{ cursor: mainStatus.details ? 'help' : 'default' }}
                      >
                        {mainStatus.status}
                      </Badge>
                      {errorStatus && (
                        <Badge 
                          bg={errorStatus.variant} 
                          title={errorStatus.details}
                          style={{ cursor: 'help' }}
                        >
                          {errorStatus.status}
                        </Badge>
                      )}
                    </div>
                  );
                })()}
              </td>
              <td style={tableCellStyle}>
              <div className="d-flex gap-2 align-items-center">
                    <span>{team.name}</span>
                  </div>
              </td>
              <td style={tableCellStyle}>{team.createdAt}</td>
              <td style={tableCellStyle}>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleShowStudents(team.name)}
                  >
                    {t('jgTeams.showStudents')}
                  </Button>
                  {team.status === 'offen' && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleCreate(team.name)}
                    >
                      Anlegen
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showConfirmDialog} onHide={() => setShowConfirmDialog(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Teams erstellen bestätigen</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ whiteSpace: 'pre-line' }}>
          {!creatingTeams ? (
            getConfirmationMessage()
          ) : (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <div>
                <strong>Teams werden erstellt...</strong>
              </div>
              <div className="text-muted mt-2">
                Dies kann einige Sekunden dauern. Bitte warten Sie.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowConfirmDialog(false)}
            disabled={creatingTeams}
          >
            Abbrechen
          </Button>
          <Button 
            variant="primary" 
            onClick={handleConfirmCreate}
            disabled={creatingTeams}
          >
            {creatingTeams ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Erstelle...
              </>
            ) : (
              'Ja'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Studierende Dialog */}
      <Modal show={showStudentsDialog} onHide={handleCloseStudentsDialog} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Studierende - {studentsData?.jgName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingStudents ? (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <div>Lade Studierende...</div>
            </div>
          ) : studentsData ? (
            <>
              <div className="mb-3">
                <Badge bg="info" className="me-2">
                  Anzahl Studierende: {studentsData.studentCount}
                </Badge>
              </div>
              
              {studentsData.students.length > 0 ? (
                <Table striped hover responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Vorname</th>
                      <th>E-Mail</th>
                      <th>Studiengang</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentsData.students
                      .sort((a, b) => a.STUD_NACHNAME.localeCompare(b.STUD_NACHNAME))
                      .map((student, index) => (
                        <tr key={index}>
                          <td>{student.STUD_NACHNAME}</td>
                          <td>{student.STUD_VORNAME}</td>
                          <td>{student.STUD_USERNAME}</td>
                          <td>{student.STG} {student.JAHR}</td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center text-muted py-4">
                  Keine Studierenden gefunden
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">
              Keine Daten verfügbar
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseStudentsDialog}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
} 